import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectProduct,
  selectSelectedShippingPriority,
  selectStoreData,
  setSelectedShippingPriority
} from '../../redux/slices/customizer/customizerSlice';
import { ITab, ITabs } from '../../types/customizer';
import MyGallery from '../MyGallery';
import If from '../If';
import './index.scss';
import { AnyObject } from '../../utils/basic';
import PrintSummary from '../PrintSummary';
import { getSelectedInfo } from '../../utils/commonRingUtils';
import axios from 'axios';
import { toast } from 'react-toastify';
import Accordion from '../AccordionNew';
import { ReactComponent as QRIcon } from '../../assets/images/qr_icon.svg';
import QRCode from 'react-qr-code';
import URLs from '../../utils/url';
import { buildStorefrontBuilderUrl, PATH_PREFIX, STAGING_BUILDER_ORIGIN, STOREFRONT_ORIGIN } from '../../config/tenant';
import { useLocation } from 'react-router-dom';

// Ink for the "Scan to take your design with you" group: the phone illustration, its
// caption and the QR code itself, which have to match each other exactly.
//
// A literal rather than a theme token because react-qr-code takes its colour as the
// `fgColor` prop and writes it into an SVG presentation attribute, where a var()
// reference is not reliably resolved — so the QR could not follow a CSS variable and
// the three would drift apart. Same value as the Ben Bridge brandColor.
const QR_INK_COLOR = '#251024';

type SummaryProps = {
  tabs: ITabs;
  isPDP?: boolean;
  pathPrefix?: string;
  navigateToUrl: (url: string, replace?: boolean) => void;
  onChange: (updated: any) => void;
};

export const formatSummaryDate = (date: string | undefined) => {
  if (!date) return '';
  const momentDate = moment(date);
  const formatted = momentDate.format('ddd. MMM. Do*');

  if (momentDate.isSame(moment(), 'day')) {
    return `Today (${formatted})`;
  } else if (momentDate.isSame(moment().add(1, 'day'), 'day')) {
    return `Tomorrow (${formatted})`;
  }
  return formatted; // Default format
};

export const getLastestShippingDate = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter((date) => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map((date) => moment(date)));
  return earliestDate.format('ddd. MMM. Do*');
};

export const REQUIRED_TAB_FIELDS: Array<string> = []; // 'ringSize', 'engraving' removed from required tabs
export const TABS_TO_AVOID = ['summary', 'data', 'none', 'placement'];

export const getRowContent = (tab: ITab, index: number, options: { [key: string]: boolean } = {}) => {
  const selectedOption = getSelectedInfo(tab);
  const selectedText = selectedOption?.text || '';
  let title = typeof tab.title === 'string' ? tab.title : typeof tab.title === 'function' && tab.title();

  if (tab.eventKey === 'shape') {
    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        {filteredTabs.map((item, index) => (
          <div className="tw-mt-1" key={index}>
            <p>{item.label || ''}</p>
            <p className="tw-justify-self-end">
              {item.content?.selectedOption?.short_name || item.content?.selectedOption?.name}
            </p>
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'finish' && selectedText.includes(' - ')) {
    const [firstFinish, secondFinish] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>First Finish</p>
          <p className="tw-justify-self-end">{firstFinish}</p>
        </div>
        <div className="tw-mt-1">
          <p>Second Finish</p>
          <p className="tw-justify-self-end">{secondFinish}</p>
        </div>
      </>
    );
  }

  if (['outsideFeature_1', 'outsideFeature_2'].includes(tab.eventKey)) {
    if (tab.eventKey === 'outsideFeature_2') {
      title = 'Secondary Outside Feature';
    }

    const filteredTabs = tab.items?.filter((item) => !TABS_TO_AVOID.includes(item.eventKey)) || [];
    return (
      <>
        <div className="tw-mt-3">
          <p>{title}</p>
          <p className="tw-justify-self-end">{tab.selectedFeature || 'None'}</p>
        </div>
        {filteredTabs.map((item, index) => (
          <div key={index}>
            <p>{item.label || ''}</p>
            {(item.eventKey == 'cerakote' && selectedOption?.url?.includes('CER-')) ||
            item.content?.selectedOption?.sku?.includes('CER-') ? (
              <p className="tw-justify-self-end">
                Cerakote{' - '}
                {(item.content?.selectedOption?.name || 'None').replace(/(\d+)mm/g, '$1 mm')}
              </p>
            ) : (
              <p className="tw-justify-self-end">
                {(item.content?.selectedOption?.name || 'None').replace(/(\d+)mm/g, '$1 mm')}
              </p>
            )}
          </div>
        ))}
      </>
    );
  }

  if (tab.eventKey === 'insideFeature') {
    return (
      <div className="tw-mt-3">
        <p>{title}</p>
        <p className="tw-justify-self-end">
          {tab.content?.selectedOption?.sku?.includes('CER-') ? 'Cerakote - ' : ''}
          {selectedText}
        </p>
      </div>
    );
  }

  if (tab.eventKey === 'logo') {
    const [school, logo, accentColor] = selectedText.split(' - ');
    return (
      <>
        <div className="tw-mt-1">
          <p>School</p>
          <p className="tw-justify-self-end">{school}</p>
        </div>
        <div className="tw-mt-1">
          <p>Logo</p>
          <p className="tw-justify-self-end">{logo}</p>
        </div>
        <div className="tw-mt-1">
          <p>Accent Color</p>
          <p className="tw-justify-self-end">{accentColor}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="tw-mt-1">
        <p>{title}</p>
        <p className="tw-justify-self-end">{selectedText}</p>
      </div>

      {tab.eventKey === 'engraving' && selectedOption?.engravingText && (
        <div className="tw-mt-1">
          <p>Engraving Text</p>
          <p className="tw-justify-self-end">{selectedOption.engravingText}</p>
        </div>
      )}
    </>
  );
};

const Summary = ({ tabs, isPDP = false, pathPrefix, navigateToUrl, onChange }: SummaryProps) => {
  const product = useAppSelector(selectProduct);
  const storeData = useAppSelector(selectStoreData);
  const location = useLocation();
  const selectedShippingPriority = useAppSelector(selectSelectedShippingPriority);
  const dispatch = useAppDispatch();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const shipDateRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState('');
  const [isTouchscreen, setIsTouchscreen] = useState(false);

  useEffect(() => {
    const isWindows = navigator.userAgent.includes('Windows');
    const isDesktopTouch = window.matchMedia('(pointer: coarse) and (min-width: 1024px)').matches;
    setIsTouchscreen(isWindows && isDesktopTouch);
  }, []);

  const triggerShipDatePulse = () => {
    const el = shipDateRef.current;
    if (!el) return;
    el.classList.remove('animate-pulse-once');
    void el.offsetWidth;
    el.classList.add('animate-pulse-once');
    const onEnd = () => {
      el.classList.remove('animate-pulse-once');
      el.removeEventListener('animationend', onEnd);
    };
    el.addEventListener('animationend', onEnd);
  };

  useEffect(() => {
    if ((window as any).__highlightShipDate) {
      (window as any).__highlightShipDate = false;
      triggerShipDatePulse();
    }
  });

  useEffect(() => {
    const handleHighlight = () => triggerShipDatePulse();
    window.addEventListener('highlight-ship-date', handleHighlight);
    return () => window.removeEventListener('highlight-ship-date', handleHighlight);
  }, []);

  /**
   * Set the selected shipping priority to the first available priority
   * only if it is not rush
   */
  useEffect(() => {
    if (!product?.shipping_options) return;

    if (product?.shipping_priority === 'rush') {
      dispatch(setSelectedShippingPriority('rush'));
    } else {
      setDefaultShippingPriority(product?.shipping_options);
    }
  }, [product]);

  const setDefaultShippingPriority = (shippingOptions: any = {}) => {
    // When same_day and next_day are both present, the UI renders them as a single 'Standard' row
    if (shippingOptions.same_day && shippingOptions.next_day) {
      dispatch(setSelectedShippingPriority('standard'));
      return;
    }
    const availablePriority =
      ['standard', 'same_day', 'next_day', 'rush'].find((priorityKey) => !!shippingOptions?.[priorityKey]) || '';
    dispatch(setSelectedShippingPriority(availablePriority));
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Summary',
    bodyClass: 'print-window'
  });

  const handleSearch = async () => {
    try {
      const response: any = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/rings/searchMagicNumber?magicNumber=${value}`
      );
      const customizeUrl = response.data.ring_data?.customize_url;
      const bestseller_id = response.data.ring_data?.bestseller_id;
      if (bestseller_id) {
        // Add bestseller parameter to the URL
        window.location.href += `&bestSeller=${bestseller_id}`;
      } else if (customizeUrl) {
        navigateToUrl(customizeUrl);
        setValue('');
      } else {
        toast.error(response.data.message?.replace('Magic Number', 'Reference Code'), { className: 'toast-error' });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleShare = async () => {
    try {
      const link = `${URLs.siteBaseUrl}${location.pathname}${location.search}`;
      // const image_link = `${URLs.assetsBaseUrl}${images[0]}`;
      const body = {
        link
        // image_link
      };
      const response = await axios.post(`${URLs.apiBaseUrl}/v2/shareStyle`, body);
      toast.success('Style shared successfully!', { className: 'toast-success' });
    } catch (error: any) {
      console.error('Error sharing style:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to share style. Please try again.';
      toast.error(errorMessage, { className: 'toast-error' });
    }
  };

  const getRingInfo = () => {
    return (
      <>
        <div className={clsx('summary-container tw-mt-3 tw-mr-3 tw-mb-3')}>
          {REQUIRED_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
        </div>
        <div className={clsx('summary-container tw-mt-3 tw-mr-3 tw-mb-3')}>
          {ADDITIONAL_TABS.map((tab: ITab, index) => (
            <React.Fragment key={index}> {getRowContent(tab, index, { isPDP })}</React.Fragment>
          ))}
        </div>
      </>
    );
  };

  const containerClass = clsx({
    // The lg:tw-pt-20 that used to be here cleared the wordmark, which was absolutely
    // positioned over the top of the page. The wordmark now sits inside the promo bar,
    // so that headroom was just empty space.
    'tw-overflow-y-auto tw-overflow-x-hidden tw-h-full tw-py-5 lg:tw-pb-0 tw-px-5 swatch-filter-border': !isPDP,
    'tw-w-full tw-h-full tw-py-1 tw-pb-4 tw-bg-blue-50 tw-text-xs summary-pdp': isPDP
  });

  const flexWrapperClass = clsx('tw-flex tw-flex-wrap summary tw-text-base', {
    'xl:tw-w-11/12 tw-ml-auto': !isPDP
  });

  const innerContainerClass = clsx('tw-text-prussian-blue', {
    'tw-w-full lg:tw-w-3/5 lg:tw-pr-8 xl:tw-w-2/3 xl:tw-pr-[15%] 2xl:tw-pr-[20%]': !isPDP,
    'tw-w-full': isPDP
  });

  // Drives where the reference-code search renders. With a gallery the search belongs
  // under the QR code on desktop and at the foot of the order column on mobile, so the
  // order details read as one uninterrupted side. Without a gallery there is no second
  // column to put it in, so the order column's copy shows at every width instead.
  const hasGallery = !!product?.images?.length;

  // Titled and subtitled because the bare input and button gave no clue what the field
  // was for. In a function to keep the sizing rationale below out of the JSX flow.
  const renderReferenceCodeSearch = () => (
    // tw-mt-6 matches the Manufacturing Rush block above so this reads as the next
    // section in that stack; it was tw-mt-12 when it sat under the QR code.
    <div className="tw-mt-6">
      {/* Centred on the same breakpoint the field and button centre on: below sm the row
          stacks and both centre via xs:tw-m-auto, so left-aligned headings sat off to one
          side of them. From sm up all four align left together. */}
      <div className="tw-text-lg tw-font-bold tw-text-center sm:tw-text-left">Compare another ring</div>
      <div className="tw-text-sm tw-mt-1 tw-text-center sm:tw-text-left">Enter Reference Code</div>
      {/* Row sizing, sm and up: the button holds its natural width (tw-shrink-0 plus
          tw-whitespace-nowrap) and the input takes whatever is left (sm:tw-flex-1 with
          sm:tw-min-w-0 so it is allowed to shrink below its default intrinsic width).
          That keeps the pair on one line at any column width.

          The input previously carried a hard sm:!tw-w-40 (160px, !important). In the QR
          column, only ~315px wide, that left the button too little for its label — first
          breaking it across two lines, then dropping it to its own row once tw-flex-wrap
          was added. Neither read well, and both came from sizing the input first and
          letting the button take the remainder. This inverts that.

          tw-flex-wrap stays purely as a backstop for a container narrower than the button
          itself. The widths this used to carry — tw-w-50 on the button, xs:!tw-w-50 on the
          input — were never real: the spacing scale has no 50, so both emitted nothing.
          Same for xs-tw-m-auto on the button, a typo for xs:tw-m-auto.

          Below sm the row is a column and the two stack full stop, so the flex sizing is
          scoped to sm: and that layout is left as it was. */}
      <div className="tw-mt-3 tw-flex tw-flex-wrap xs:tw-flex-col sm:tw-flex-row tw-gap-3">
        <input
          type="text"
          name="magic_number"
          placeholder="000-03C9-10"
          value={value}
          onChange={(e) => {
            const formatted = e.target.value.replace(/O/gi, '0');
            setValue(formatted);
          }}
          className="tw-text-sm tw-p-3 tw-border tw-border-solid tw-border-primary-border-color sm:tw-flex-1 sm:tw-min-w-0 xs:tw-m-auto sm:tw-m-0 tw-rounded-none"
        />
        <button
          id="search-btn"
          className="tw-btn tw-btn-brand tw-btn-sm tw-shrink-0 tw-whitespace-nowrap xs:tw-m-auto sm:tw-m-0 tw-rounded-none"
          onClick={handleSearch}
        >
          Search Reference Code
        </button>
      </div>
    </div>
  );

  const REQUIRED_TABS = tabs.filter((tab: ITab) => REQUIRED_TAB_FIELDS.includes(tab.eventKey));
  const ADDITIONAL_TABS = tabs.filter(
    (tab: ITab) => !REQUIRED_TAB_FIELDS.includes(tab.eventKey) && !TABS_TO_AVOID.includes(tab.eventKey)
  );

  /**
   * If the shipping priority is rush, add the shipping priority to the url.
   * If the shipping priority is not rush, remove the shipping priority from the url.
   */
  const updateShippingPriority = (priority: string) => {
    dispatch(setSelectedShippingPriority(priority));
    if (priority === 'rush') {
      onChange({ shipping_priority: 'rush' });
    } else {
      onChange({ shipping_priority: undefined });
    }
  };

  return (
    <div className={containerClass}>
      <div className={flexWrapperClass}>
        <div className={innerContainerClass}>
          {/* Heads the order column, in the space the reference-code search used to
              occupy before it moved to the QR column. Centred below sm, the same breakpoint
              the reference-code heading and its field use, so everything in this column
              switches alignment together rather than at two different widths. */}
          <div className="tw-text-xl tw-font-bold tw-mt-2 tw-mb-5 tw-text-center sm:tw-text-left">
            Congratulations on creating something uniquely yours
          </div>

          {/* INFORMATION FOR ORDERING */}
          <div className="tw-border tw-border-solid tw-border-primary-border-color tw-rounded-none tw-mt-4 tw-pb-5">
            <div className="tw-text-lg tw-font-bold tw-px-6 tw-pt-5 tw-pb-3">Information for Ordering</div>
            <div className="summary-container">
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Ring Category Sku</p>
                <p className="tw-justify-start">{product?.helzberg_custom_sku}</p>
              </div>
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Reference Code</p>
                <p className="tw-justify-start">{product?.magic_number}</p>
              </div>
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className="tw-w-40 tw-shrink-0 tw-mr-3">Description</p>
                <p className="tw-justify-start">{product?.description}</p>
              </div>
            </div>
          </div>

          {/* RING INFO */}
          <Accordion
            title="Your Custom Ring Details"
            className="summary-accordian tw-mt-4 tw-border tw-border-solid tw-border-primary-border-color tw-rounded-none"
          >
            {getRingInfo()}
          </Accordion>

          {/* SHIP DATE SECTION */}
          <If condition={!!storeData?.can_select_shipping_priority}>
            <div
              ref={shipDateRef}
              className="tw-border tw-border-solid tw-border-primary-border-color tw-rounded-none tw-p-6 tw-mt-6"
            >
              <div className="tw-text-lg tw-font-bold tw-pb-3">Choose Your Shipping Date</div>
              <div className="summary-container">
                {product?.shipping_options?.same_day && product?.shipping_options?.next_day ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">Standard</p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(
                        moment(product?.shipping_options?.same_day).isBefore(
                          moment(product?.shipping_options?.next_day)
                        )
                          ? product?.shipping_options?.same_day
                          : product?.shipping_options?.next_day
                      )}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'standard'}
                        onChange={() => updateShippingPriority('standard')}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    {product?.shipping_options?.same_day ? (
                      <div className="tw-mt-1">
                        <p className="tw-font-medium">Same Day</p>
                        <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                          {formatSummaryDate(product?.shipping_options?.same_day)}
                          <input
                            type="radio"
                            name="shipping-priority"
                            checked={selectedShippingPriority === 'same_day'}
                            onChange={() => updateShippingPriority('same_day')}
                          />
                        </label>
                      </div>
                    ) : null}
                    {product?.shipping_options?.next_day ? (
                      <div className="tw-mt-1">
                        <p className="tw-font-medium">Next Day</p>
                        <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                          {formatSummaryDate(product?.shipping_options?.next_day)}
                          <input
                            type="radio"
                            name="shipping-priority"
                            checked={selectedShippingPriority === 'next_day'}
                            onChange={() => updateShippingPriority('next_day')}
                          />
                        </label>
                      </div>
                    ) : null}
                  </>
                )}
                {product?.shipping_options?.standard ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">Without Rush</p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(product?.shipping_options?.standard)}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'standard'}
                        onChange={() => updateShippingPriority('standard')}
                      />
                    </label>
                  </div>
                ) : null}
                {product?.shipping_options?.rush ? (
                  <div className="tw-mt-1">
                    <p className="tw-font-medium">
                      With Rush{' '}
                      {product?.shipping_options?.rush_fee ? `(+ $${product?.shipping_options?.rush_fee})` : ''}
                    </p>
                    <label className="tw-justify-self-end tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                      {formatSummaryDate(product?.shipping_options?.rush)}
                      <input
                        className="shipping-radio"
                        type="radio"
                        name="shipping-priority"
                        checked={selectedShippingPriority === 'rush'}
                        onChange={() => updateShippingPriority('rush')}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
              <div className="tw-pt-5 tw-text-xs">
                {product?.shipping_options?.rush
                  ? '*Every ring is made to order. By selecting "Rush", we expedite the manufacturing of your ring. If you need a rush sooner than the dates displayed, contact Bridal Team.'
                  : '*Every ring is made to order. If you need a rush sooner than the date displayed, contact Bridal Team.'}
              </div>
            </div>
          </If>

          {/* With the other order-information sections at every width, per Ben Bridge.
              Was two breakpoint-split copies: here below Print, and under the QR code. */}
          {renderReferenceCodeSearch()}

          <>
            <button
              id="print-btn"
              className="tw-btn tw-btn-brand tw-p-3 tw-w-40 tw-my-6 tw-rounded-none"
              onClick={handlePrint}
            >
              Print
            </button>
            <PrintSummary tabs={tabs} image={product?.images?.[0]} pathPrefix={pathPrefix} ref={componentRef} />
          </>
        </div>
        {hasGallery && (
          <div className="tw-flex-1 gallery_container tw-relative tw-hidden lg:tw-flex tw-flex-col">
            <MyGallery images={product?.images} />
            {/* QR Code Sharing Section */}
            <div className="tw-mt-6 xl:tw-w-3/4 tw-m-auto">
              {/* Top Section: Icon/Text and QR Code */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                {/* Selfie Icon and Text - Centered */}
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-flex-1">
                  <QRIcon className="tw-w-20 tw-h-20" style={{ color: QR_INK_COLOR }} />
                  <span
                    className="tw-text-sm tw-italic tw-text-center tw-leading-tight"
                    style={{ color: QR_INK_COLOR }}
                  >
                    Scan to take your design with you
                  </span>
                </div>

                {/* QR Code - Far Right */}
                <div className="tw-flex tw-justify-end tw-flex-shrink-0">
                  {(() => {
                    // "Scan to take your design with you" — encodes a URL that
                    // reopens this exact ring configuration on a phone.
                    //
                    // Mirrors upstream's Helzberg branch: redirect_url wins, then the
                    // Ben Bridge target for this environment, then siteBaseUrl.
                    const isStagingApi = (process.env.REACT_APP_API_BASE_URL || '').includes('staging');
                    let qrUrl: string;
                    const redirectUrl = new URLSearchParams(location.search).get('redirect_url');
                    if (redirectUrl) {
                      let origin = URLs.siteBaseUrl;
                      try {
                        origin = new URL(redirectUrl).origin;
                      } catch {
                        // redirect_url malformed; fall back to siteBaseUrl
                      }
                      qrUrl = `${origin}${location.pathname}${location.search}`;
                    } else if (pathPrefix === PATH_PREFIX) {
                      if (isStagingApi) {
                        qrUrl = `${STAGING_BUILDER_ORIGIN}${location.pathname}${location.search}`;
                      } else if (new URLSearchParams(location.search).has('ring_url')) {
                        // Already on the ProductBuilder URL (ring path lives in the ring_url
                        // param); just retarget the origin instead of re-wrapping it.
                        qrUrl = `${STOREFRONT_ORIGIN}${location.pathname}${location.search}`;
                      } else {
                        qrUrl = buildStorefrontBuilderUrl(location.pathname, location.search);
                      }
                    } else {
                      qrUrl = `${URLs.siteBaseUrl}${location.pathname}${location.search}`;
                    }
                    console.log('[QR Code URL]', qrUrl);
                    // Two sizes rather than one compromise. 120 is the base: the row is
                    // a fixed width, so every pixel the QR gives up goes to the
                    // illustration and caption beside it, which were wrapping to four
                    // lines at 140. Past 2xl the column is wide enough that the caption
                    // fits on one line with room to spare, so the QR takes 160 there.
                    //
                    // The class scales it instead of the size prop because the prop is a
                    // single number — react-qr-code spreads unknown props onto its <svg>,
                    // which carries a viewBox, so CSS width/height override the attributes
                    // and rescale the code without distorting it. size={120} stays as the
                    // floor every breakpoint below 2xl uses.
                    return (
                      <QRCode value={qrUrl} size={120} fgColor={QR_INK_COLOR} className="2xl:tw-w-40 2xl:tw-h-40" />
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
